import React, { useEffect, useRef, useState, createContext, useContext } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InstituteSeo from "@/components/InstituteSeo";
import api from "@/lib/api";
import { slugify } from "./_shared";
import AdmissionInquiryModal from "./AdmissionInquiryModal";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import {
  Phone, Mail, MapPin, ExternalLink, Menu, X,
  Facebook, Instagram, Linkedin, Twitter, Youtube, ArrowUpRight,
  ChevronDown, GraduationCap, Sparkles,
} from "lucide-react";

const SLUG = "nursing";

const Ctx = createContext(null);
export const useNursing = () => useContext(Ctx);

// Flat nav (mirrors /polyaurangabad and /foodtech)
const NAV = [
  { to: "", end: true, label: "Home" },
  { to: "about", label: "About" },
  { to: "departments", label: "Departments" },
  { to: "campus-engagements", label: "Campus Engagements" },
  { to: "committees", label: "Committees & Cells" },
  { to: "gallery", label: "Gallery" },
  { to: "facilities", label: "Facilities" },
  { to: "academics", label: "Academics" },
  { to: "alumni", label: "Alumni" },
  { to: "careers", label: "Careers" },
  { to: "fra-ffc", label: "FRA/FFC" },
  { to: "contact", label: "Contact" },
];

export default function CidcoLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/website/${SLUG}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Website not configured");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[#0F2A5F] font-['Inter',sans-serif]">
        <Sparkles className="animate-pulse" size={40} strokeWidth={1.5} />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 font-['Inter',sans-serif]">
        <div className="text-center max-w-md">
          <Sparkles className="mx-auto text-[#0F2A5F]" size={36} strokeWidth={1.5} />
          <h1 className="mt-4 font-['Poppins',sans-serif] font-bold text-2xl text-[#0F172A]">Website unavailable</h1>
          <p className="mt-2 text-sm text-[#475569]">{err || "Please try again later."}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute || {};
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  return (
    <Ctx.Provider value={data}>
      <InstituteSeo slug="nursing" inst={inst} themeColor="#0F172A" ogImage="https://gsmandal.org/assets/og/mit-nursing-og.jpg" />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@500;600;700;800&display=swap"
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-white text-[#0F172A] font-['Inter',sans-serif]">
        {/* Utility top bar */}
        <div className="bg-[#0A1F47] text-white/85 text-[12px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-5 flex-wrap">
              {inst.admission_phone && (
                <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-[#0FB28A]" data-testid="nur-top-phone">
                  <Phone size={11} strokeWidth={1.8} className="text-[#0FB28A]" />{inst.admission_phone}
                </a>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0FB28A]" data-testid="nur-top-email">
                  <Mail size={11} strokeWidth={1.8} className="text-[#0FB28A]" />{inst.email}
                </a>
              )}
              {inst.city && (
                <span className="hidden md:inline-flex items-center gap-1.5">
                  <MapPin size={11} strokeWidth={1.8} className="text-[#0FB28A]" />{inst.city}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <InstituteSwitcher currentSlug="nursing" testidPrefix="nur-top-switcher" />
              {inst.facebook_url && (<a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="nur-top-facebook" className="hover:text-[#0FB28A]"><Facebook size={12} strokeWidth={1.8} /></a>)}
              {inst.instagram_url && (<a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="nur-top-instagram" className="hover:text-[#0FB28A]"><Instagram size={12} strokeWidth={1.8} /></a>)}
              {inst.linkedin_url && (<a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" data-testid="nur-top-linkedin" className="hover:text-[#0FB28A]"><Linkedin size={12} strokeWidth={1.8} /></a>)}
              {inst.twitter_url && (<a href={inst.twitter_url} target="_blank" rel="noopener noreferrer" data-testid="nur-top-twitter" className="hover:text-[#0FB28A]"><Twitter size={12} strokeWidth={1.8} /></a>)}
              {inst.youtube_url && (<a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="nur-top-youtube" className="hover:text-[#0FB28A]"><Youtube size={12} strokeWidth={1.8} /></a>)}
              <a href="https://erp.mit.asia" target="_blank" rel="noopener noreferrer" data-testid="nur-top-erp"
                 className="inline-flex items-center gap-1 bg-[#0FB28A] hover:bg-[#0E916F] text-white px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold">
                Student ERP <ArrowUpRight size={10} strokeWidth={2.5} />
              </a>
              {(data?.naac_documents || []).length > 0 && (
                <NavLink to="naac" data-testid="nur-top-naac"
                  className="inline-flex items-center gap-1 bg-emerald-200 hover:bg-emerald-100 text-[#0F2A5F] px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ring-1 ring-emerald-400">
                  NAAC
                </NavLink>
              )}
            </div>
          </div>
        </div>

        {/* Header band */}
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-1 md:grid-cols-[minmax(0,19fr)_minmax(0,63fr)_minmax(0,18fr)] items-center gap-4">
            <NavLink to="" end className="flex items-center justify-center md:justify-start" data-testid="nur-logo-link">
              <img src={logoSrc} alt={inst.name} className="h-[96px] md:h-[112px] w-auto max-w-full object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#0FB28A] font-semibold" data-testid="nur-header-trust">
                Gramaudyogik Shikshan Mandal&apos;s
              </div>
              <div className="font-['Poppins',sans-serif] font-bold text-xl md:text-3xl text-[#0F2A5F] uppercase mt-1" data-testid="nur-header-name">
                {inst.name}
              </div>
              {inst.city && (
                <div className="font-['Poppins',sans-serif] text-base md:text-lg text-[#1B3E83] mt-1" data-testid="nur-header-city">
                  {inst.city}
                </div>
              )}
              {inst.tagline && (
                <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#0FB28A] italic mt-2">{inst.tagline}</div>
              )}
            </div>
            <div className="hidden md:block text-right">
              {inst.code && (
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#475569]" data-testid="nur-header-code">
                  <span className="text-[#0F2A5F] font-semibold">Institute Code</span> · {inst.code}
                </div>
              )}
              <div className="text-xs text-[#475569] mt-0.5">
                AICTE Approved{inst.year_of_establishment ? ` · Estd. ${inst.year_of_establishment}` : ""}
              </div>
              {inst.mandatory_disclosure_url && (
                <a href={inst.mandatory_disclosure_url} target="_blank" rel="noopener noreferrer"
                   data-testid="nur-header-mandatory-disclosure"
                   className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#0FB28A] hover:text-[#0F2A5F] underline underline-offset-2">
                  Mandatory Disclosure <ExternalLink size={10} strokeWidth={2} />
                </a>
              )}
            </div>
          </div>

          {/* Main nav — navy strip with mega-menu dropdowns */}
          <nav className="bg-[#0F2A5F] shadow-md" data-testid="nur-nav">
            <div className="max-w-7xl mx-auto px-4">
              <div className="hidden xl:flex items-center gap-1 py-2 flex-wrap">
                {NAV.map((n) =>
                  n.to === "departments" ? (
                    <DepartmentsMegaMenu key="departments" departments={data.departments || []} />
                  ) : n.to === "campus-engagements" ? (
                    <EngagementsDropdown key="campus-engagements" events={data.events || []} />
                  ) : n.to === "about" ? (
                    <AboutMenu
                      key="about"
                      linkClass={navLinkClass}
                      testidPrefix="nur-nav-about"
                      menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                      itemHoverClassName="hover:bg-emerald-50 text-slate-800"
                    />
                  ) : (
                    <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nur-nav-${n.to || "home"}`} className={navLinkClass}>
                      {n.label}
                    </NavLink>
                  )
                )}
              </div>
              <div className="xl:hidden flex items-center justify-between py-3">
                <span className="text-white font-medium text-sm">Menu</span>
                <button type="button" onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Toggle menu" data-testid="nur-nav-toggle">
                  {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              </div>
              {mobileOpen && (
                <div className="xl:hidden grid grid-cols-2 gap-1 pb-3">
                  {NAV.map((n) =>
                    n.to === "departments" ? (
                      <React.Fragment key="departments">
                        <NavLink to="departments" onClick={() => setMobileOpen(false)} className={navLinkClass}>Departments</NavLink>
                        {(data.departments || []).length > 0 && (
                          <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                            {(data.departments || []).map((d) => (
                              <NavLink key={d.id} to={`departments/${slugify(d.name)}`} onClick={() => setMobileOpen(false)}
                                className="text-[12px] text-white/80 hover:text-[#7FE7CE] py-1.5 border-l-2 border-white/15 pl-3">
                                {d.name}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    ) : n.to === "campus-engagements" ? (
                      <React.Fragment key="campus-engagements">
                        <NavLink to="campus-engagements" onClick={() => setMobileOpen(false)} className={navLinkClass}>Campus Engagements</NavLink>
                        {(() => {
                          const types = uniqueTypesWithCount(data.events || []);
                          return types.length > 0 ? (
                            <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                              {types.map((t) => (
                                <NavLink key={t.slug} to={`campus-engagements/${t.slug}`} onClick={() => setMobileOpen(false)}
                                  className="text-[12px] text-white/80 hover:text-[#7FE7CE] py-1.5 border-l-2 border-white/15 pl-3">
                                  {t.label} ({t.count})
                                </NavLink>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </React.Fragment>
                    ) : n.to === "about" ? (
                      <AboutMenu
                        key="about"
                        linkClass={navLinkClass}
                        testidPrefix="nur-nav-about"
                        menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                        itemHoverClassName="hover:bg-emerald-50 text-slate-800"
                      />
                    ) : (
                      <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)} className={navLinkClass}>
                        {n.label}
                      </NavLink>
                    )
                  )}
                  {(data?.naac_documents || []).length > 0 && (
                    <NavLink to="naac" onClick={() => setMobileOpen(false)} data-testid="nur-nav-naac-mobile"
                      className="col-span-2 inline-flex items-center justify-center gap-1.5 bg-emerald-300 hover:bg-emerald-200 text-slate-900 font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-sm shadow-sm ring-2 ring-emerald-300">
                      NAAC
                    </NavLink>
                  )}
                  <button type="button" onClick={() => { setMobileOpen(false); setInquiryOpen(true); }} data-testid="nur-nav-apply-mobile"
                    className="col-span-2 mt-2 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#0FB28A] to-[#0E916F] text-white font-semibold text-xs uppercase tracking-wider px-3 py-2 rounded-lg shadow-md">
                    Apply Now
                  </button>
                </div>
              )}
            </div>
          </nav>

          {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
            <ScrollerBar items={inst.scrollers} />
          )}
        </header>

        <main className="flex-1">
          <Outlet />
        </main>

        {inst.whatsapp_phone && (
          <WhatsAppFab phone={inst.whatsapp_phone.replace(/[^0-9]/g, "")} message={`Hello ${inst.name || "team"}, I have an admission inquiry.`} />
        )}
        <AdmissionInquiryFab onClick={() => setInquiryOpen(true)} />
        <AdmissionInquiryModal
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          slug={inst.website_slug}
          departments={data.departments || []}
        />

        {/* Footer */}
        <footer className="bg-[#0A1F47] text-white/80 mt-16">
          <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 text-[#0FB28A] font-semibold">
                <GraduationCap size={18} strokeWidth={2} />
                <span className="uppercase tracking-[0.2em] text-xs">{inst.name || "MIT Nursing College"}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                A flagship nursing institution of Gramaudyogik Shikshan Mandal — building India&apos;s next generation of technologists.
              </p>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm font-['Poppins',sans-serif]">Explore</div>
              <ul className="space-y-2 text-sm">
                {[
                  { to: "about", l: "About" },
                  { to: "vision-mission", l: "Vision & Mission" },
                  { to: "leadership", l: "Leadership" },
                  { to: "departments", l: "Departments" },
                  { to: "admissions", l: "Admissions" },
                  { to: "contact", l: "Contact" },
                ].map((p) => (
                  <li key={p.to}><NavLink to={p.to} className="hover:text-[#0FB28A]">{p.l}</NavLink></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm font-['Poppins',sans-serif]">Engage</div>
              <ul className="space-y-2 text-sm">
                <li><NavLink to="campus-engagements" className="hover:text-[#0FB28A]">Campus Engagements</NavLink></li>
                <li><NavLink to="committees" className="hover:text-[#0FB28A]">Committees &amp; Cells</NavLink></li>
                <li><NavLink to="gallery" className="hover:text-[#0FB28A]">Gallery</NavLink></li>
                <li><NavLink to="notices" className="hover:text-[#0FB28A]">Notices</NavLink></li>
                <li><NavLink to="links" className="hover:text-[#0FB28A]">Important Links</NavLink></li>
                <li><NavLink to="facilities" className="hover:text-[#0FB28A]">Facilities</NavLink></li>
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm font-['Poppins',sans-serif]">Reach Us</div>
              <ul className="space-y-2 text-sm">
                {(inst.address1 || inst.city) && (
                  <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 text-[#0FB28A] shrink-0" />
                    {[inst.address1, inst.address2, [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "), inst.state, inst.country || "INDIA"].filter(Boolean).join(", ")}
                  </li>
                )}
                {(inst.contact_phone || inst.admission_phone) && (
                  <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 text-[#0FB28A]" />
                    {[inst.contact_phone, inst.admission_phone].filter(Boolean).join(" · ")}
                  </li>
                )}
                {inst.email && (
                  <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 text-[#0FB28A]" />
                    <a href={`mailto:${inst.email}`} className="hover:text-[#0FB28A]">{inst.email}</a>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/60">
            © {new Date().getFullYear()} {inst.name} · Gramaudyogik Shikshan Mandal. All rights reserved.
          </div>
        </footer>
      </div>
    </Ctx.Provider>
  );
}

const navLinkClass = ({ isActive }) =>
  `text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap font-medium ${
    isActive
      ? "bg-[#0FB28A] text-white shadow-sm"
      : "text-white/90 hover:bg-white/10 hover:text-white"
  }`;

function DepartmentsMegaMenu({ departments }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

  const sorted = [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div ref={wrapRef} className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="nur-nav-departments-wrap"
    >
      <NavLink to="departments" data-testid="nur-nav-departments"
        className={({ isActive }) => `${navLinkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}>
        Departments
        <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      {open && sorted.length > 0 && (
        <div className="absolute left-0 top-full pt-2 z-50"
             data-testid="nur-nav-departments-menu"
             onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="bg-white rounded-xl shadow-2xl ring-1 ring-[#E2E8F0] p-5 w-[640px]">
            <div className="flex items-center justify-between mb-4 border-b border-[#E2E8F0] pb-3">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-semibold">Academic Programmes</div>
                <div className="font-['Poppins',sans-serif] font-bold text-lg text-[#0F2A5F]">Explore Departments</div>
              </div>
              <NavLink to="departments" onClick={() => setOpen(false)}
                className="text-[11px] uppercase tracking-wider font-semibold text-[#0FB28A] hover:text-[#0F2A5F] inline-flex items-center gap-1">
                View All <ArrowUpRight size={12} />
              </NavLink>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {sorted.map((d) => (
                <NavLink key={d.id} to={`departments/${slugify(d.name)}`}
                  data-testid={`nur-nav-dept-${d.id}`}
                  onClick={() => setOpen(false)}
                  className="group flex items-start gap-3 p-3 rounded-lg hover:bg-[#F4F6FA] transition-colors">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-[#0F2A5F] to-[#1B3E83] text-[#0FB28A] flex items-center justify-center group-hover:from-[#0FB28A] group-hover:to-[#0E916F] group-hover:text-white transition-colors">
                    <GraduationCap size={16} strokeWidth={1.5} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-[#0F2A5F] group-hover:text-[#0FB28A] transition-colors">{d.name}</span>
                    {d.code && <span className="block text-[10px] text-[#475569] font-mono uppercase tracking-wider mt-0.5">{d.code}</span>}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function uniqueTypesWithCount(events) {
  const map = new Map();
  for (const e of events) {
    const label = (e.event_type || "").trim();
    if (!label) continue;
    const slug = slugify(label);
    if (map.has(slug)) map.set(slug, { ...map.get(slug), count: map.get(slug).count + 1 });
    else map.set(slug, { label, slug, count: 1 });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function EngagementsDropdown({ events }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

  const types = uniqueTypesWithCount(events);

  return (
    <div ref={wrapRef} className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="nur-nav-engagements-wrap">
      <NavLink to="campus-engagements" data-testid="nur-nav-campus-engagements"
        className={({ isActive }) => `${navLinkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}>
        Campus Engagements
        {types.length > 0 && (<ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />)}
      </NavLink>
      {open && types.length > 0 && (
        <div className="absolute left-0 top-full pt-2 min-w-[260px] z-50"
             data-testid="nur-nav-engagements-menu"
             onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="bg-white rounded-xl shadow-2xl ring-1 ring-[#E2E8F0] py-2">
            <NavLink to="campus-engagements" data-testid="nur-nav-engagements-all"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-[#0F2A5F] hover:bg-[#F4F6FA] border-b border-[#E2E8F0]">
              <div className="font-semibold">All Stories</div>
              <div className="text-[10px] uppercase tracking-wider text-[#475569] mt-0.5">Show every type</div>
            </NavLink>
            {types.map((t) => (
              <NavLink key={t.slug} to={`campus-engagements/${t.slug}`}
                data-testid={`nur-nav-engagement-type-${t.slug}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-[#0F2A5F] hover:bg-[#F4F6FA]">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{t.label}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#0FB28A] font-mono font-semibold">{t.count}</span>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppFab({ phone, message }) {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      aria-label="Chat on WhatsApp for admission inquiry"
      data-testid="nur-whatsapp-fab"
      className="fixed z-40 bottom-24 right-5 sm:bottom-28 sm:right-7 flex items-center gap-2 group">
      <span className="absolute inline-flex h-14 w-14 rounded-full bg-green-500 opacity-40 animate-ping pointer-events-none right-0 bottom-0" />
      <span className="hidden sm:inline-flex bg-white text-[#0F2A5F] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-opacity">
        Admission inquiry
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .15 5.34.15 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.91-5.34 11.91-11.9 0-3.18-1.24-6.17-3.45-8.42ZM12.06 21.8h-.01a9.85 9.85 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.23-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.87 9.87 0 0 1 2.9 7c0 5.46-4.44 9.89-9.9 9.89Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5l-.56-.01c-.2 0-.5.07-.76.37-.27.3-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.18 3 .15.2 2.06 3.15 5 4.42.7.3 1.24.48 1.66.62.7.22 1.34.19 1.84.12.56-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
        </svg>
      </span>
    </a>
  );
}

function AdmissionInquiryFab({ onClick }) {
  return (
    <button type="button" onClick={onClick}
      aria-label="Open admission inquiry form"
      data-testid="nur-admission-inquiry-fab"
      className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 group">
      <span className="hidden sm:inline-flex bg-white text-[#0F2A5F] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-opacity">
        Contact Admissions
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-[#0FB28A] to-[#0E916F] hover:from-[#0E916F] hover:to-[#0a7757] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
          <path d="M22 10v6" />
          <path d="M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
      </span>
    </button>
  );
}

function ScrollerBar({ items }) {
  const list = items || [];
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-3">
        <span className="opacity-80 mr-2 align-middle">▸</span>
        {s.text}
      </span>
    );
    if (s.url) {
      return (
        <a key={`${s.id}-${i}`} href={s.url} target="_blank" rel="noopener noreferrer"
          className="hover:underline focus:underline outline-none"
          data-testid={`nur-scroller-link-${s.id}`}>
          {inner}
        </a>
      );
    }
    return <span key={`${s.id}-${i}`} data-testid={`nur-scroller-text-${s.id}`}>{inner}</span>;
  };
  const duration = Math.max(20, Math.min(60, list.length * 12));
  return (
    <div className="bg-gradient-to-r from-[#0FB28A] to-[#0E916F] text-white border-y border-[#0FB28A]/40 overflow-hidden" data-testid="nur-scroller-bar">
      <style>{`
        @keyframes nur-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [data-testid="nur-scroller-bar"]:hover [data-testid="nur-scroller-track"] {
          animation-play-state: paused !important;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded shrink-0">
          Announcement
        </span>
        <div className="flex-1 overflow-hidden">
          <span data-testid="nur-scroller-track"
            className="inline-block whitespace-nowrap text-sm font-medium"
            style={{ animation: `nur-marquee ${duration}s linear infinite` }}>
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}
